/*
 * CostCenterForm.tsx : Un formulaire pour ajouter ou modifier un centre de coûts.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube } from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	ToggleButtonField,
	useFormDirty,
} from '../../../common';

// Import Centre de coûts
import { CostCenterData } from '../types/types';
import { costCentersApi } from '../services/costCentersApi';

// Import Centre de coûts - Catégories
import { costCenterCategoriesApi } from '../../costCenterCategories/services/costCenterCategoriesApi';

// Composant CostCenterForm
const CostCenterForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<CostCenterData>({
		id: 0,
		label: '',
		code: '',
		exercise: new Date().getFullYear(),
		active: true,
		categorie: { id: 0, label: '' },
	});
	const [categoryOptions, setCategoryOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// On commence par charger les catégories de centres de coûts
		costCenterCategoriesApi
			.listSelect(token)
			.then((items) => {
				const options = items.map((item) => ({
					key: item.id.toString(),
					label: String(item.label),
				}));
				setCategoryOptions(options);
			})
			.catch((error) => console.error(error));

		if (id) {
			costCentersApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion des changements dans le formulaire pour les selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		// Si name est categorie, on doit mettre à jour l'objet complet
		if (name === 'categorie') {
			const selectedOption = categoryOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData({
					...formData,
					categorie: {
						id: selectedOption.key as unknown as number,
						label: selectedOption.label,
					},
				});
			}
			return;
		}
		// Pour les autres champs, mise à jour standard
		setFormData({ ...formData, [name]: value });
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/settings/cost-centers');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			costCentersApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			costCentersApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/settings/cost-centers/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<div className="flex items-start mb-3 space-x-4">
					{/* Icône à gauche */}
					<FontAwesomeIcon
						icon={faCube}
						className="w-20 h-20 text-blue-900/90"
					/>

					{/* Titre + toggle à droite */}
					<div className="flex flex-col mt-3">
						<h1 className="text-3xl font-bold">
							{id ? "Édition d'un centre de coûts" : 'Nouveau centre de coûts'}
						</h1>
						{/* Toggle “Actif” sous le titre */}
						<div className="mt-2">
							<ToggleButtonField
								label="Actif"
								isOn={formData.active ? true : false}
								onChange={(value) =>
									setFormData({ ...formData, active: value })
								}
								invert={true}
								size="sm"
								loading={loading}
							/>
						</div>
					</div>
				</div>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Label"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "Annuités d&apos;amortissements Couvercle Cuve"'
								/>
								<InputField
									label="Code"
									name="code"
									value={formData.code}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "F022PZAMCO"'
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<InputField
									label="Exercice"
									name="exercise"
									type="number"
									value={formData.exercise}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "2025"'
								/>
								<SelectField
									label="Catégorie"
									selectedKey={formData.categorie?.id.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'categorie',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={categoryOptions}
									loading={loading}
									entityHref={
										formData.categorie?.id
											? `/app/settings/cost-center-categories/${formData.categorie.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											categorie: null,
										}))
									}
								/>
							</div>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate('/app/settings/cost-centers')}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default CostCenterForm;
